import { clerkSearchWager, getDepositTicketOpenBets, getDepositTicketGradedBets } from '@/utils/api';
import i18n from '@/utils/i18n';
import Vue from 'vue';
import Vuex from 'vuex';
import { getField, updateField } from 'vuex-map-fields';
import moment from 'moment';
import { formatOpenBetsData } from '@/views/history/history-utils';

Vue.use(Vuex);

function getCustomRanges() {
  return [
    {
      name: i18n.t('GamesRangePicker.Today'),
      rangeStart: moment(),
      rangeEnd: moment(),
    },
    {
      name: i18n.t('GamesRangePicker.Yesterday'),
      rangeStart: moment().add(-1, 'days'),
      rangeEnd: moment().add(-1, 'days'),
    },
    {
      name: i18n.t('GamesRangePicker.Last Week'),
      rangeStart: moment()
        .add(-1, 'weeks')
        .startOf('isoWeek'),
      rangeEnd: moment()
        .add(-1, 'weeks')
        .add(1, 'days')
        .endOf('isoWeek'),
    },
    {
      name: i18n.t('GamesRangePicker.This Month'),
      rangeStart: moment().startOf('month'),
      rangeEnd: moment().endOf('month'),
    },
    {
      name: i18n.t('GamesRangePicker.Last 3 Months'),
      rangeStart: moment()
        .add(-2, 'M')
        .startOf('month'),
      rangeEnd: moment().endOf('month'),
    },
    {
      name: i18n.t('GamesRangePicker.This Year'),
      rangeStart: moment().startOf('year'),
      rangeEnd: moment().endOf('year'),
    },
  ];
}

const getDefaultState = () => {
  return {
    amount: 25,
    invalidScanCode: false,
    depositStatus: {
      loading: false,
      success: false,
      error: false,
    },
    selectedRange: 0,
    selectedRangeFilter: {
      startDate: new Date(moment()),
      endDate: new Date(moment()),
    },
    clerkWagerSearchStatus: {
      loading: false,
      success: false,
      error: false,
    },
    clerkScanCodeStatus: {
      loading: false,
      success: false,
      error: false,
    },
    ticketOpenBets: [],
    ticketGradedBets: [],
    clerkWagerSearchResult: [],
    scanCode: null,
    scanDetailResponse: null,
  };
};
const state = getDefaultState();

const getters = {
  getField,
  clerkWagerTableFields: _ => [
    {
      key: 'TicketNumber',
      label: i18n.t('BetSlip.Bet Ticket'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'CompleteDescription',
      label: i18n.t('Type'),
      _searchable: true,
      _sortable: false,
    },
    {
      key: 'Status',
      label: i18n.t('Status'),
      _searchable: true,
      _sortable: false,
    },
    {
      key: 'OriginalRiskAmount',
      label: i18n.t('Risk'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'OriginalWinAmount',
      label: i18n.t('Win'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'PlacedDate',
      label: i18n.t('Placed'),
      _searchable: false,
      _sortable: true,
      thStyle: { width: '170px' },
      _sortDirection: 'desc',
    },
    {
      key: 'action',
      label: i18n.t('Action'),
      _searchable: false,
      _sortable: false,
      // class: "d-none"
    },
  ],
  customRanges: _ => [],
};

const mutations = {
  updateField,
  updateClerkWagerSearchStatus(state, row) {
    state.clerkWagerSearchStatus = {
      ...state.clerkWagerSearchStatus,
      ...row,
    };
  },
  setClerkWagerSearchResult(state, arr) {
    state.clerkWagerSearchResult = arr;
  },
  setClerkScanCodeStatus(state, row) {
    state.clerkScanCodeStatus = {
      ...state.clerkScanCodeStatus,
      ...row,
    };
  },
  setTicketOpenBets(state, response) {
    state.ticketOpenBets = response;
  },
  setTicketGradedBets(state, response) {
    state.ticketGradedBets = response;
  },
  setScanDetailResponse(state, response) {
    state.scanDetailResponse = response;
  },
  setScanCode(state, data) {
    state.scanCode = data;
  },
  setInvalidScanCode(state, row) {
    state.invalidScanCode = row;
  },
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
};

const actions = {
  tryClerkSearchWager({ commit, rootState, getters, rootGetters }, selectedTerminalId) {
    commit('updateClerkWagerSearchStatus', { loading: true });
    commit('setClerkWagerSearchResult', []);
    const selectedRangeFilter = state.selectedRangeFilter;
    let from;
    let to;

    if (selectedRangeFilter.startDate && selectedRangeFilter.endDate) {
      from = moment(selectedRangeFilter.startDate)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm');
      to = moment(selectedRangeFilter.endDate)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm');
    } else {
      from = getters['customRanges'][state.selectedRange].startDate;
      to = getters['customRanges'][state.selectedRange].endDate;
    }

    const formData = {
      mac_address: rootState.settings.macAddress,
      from,
      to,
      terminal_id: selectedTerminalId || rootState.settings.terminalInfo.terminal_id,
    };
    clerkSearchWager(formData).then(r => {
      if (r.data.IsSuccess) {
        const currencySymbol = rootGetters['player/currencySymbol'];
        commit('setClerkWagerSearchResult', formatOpenBetsData(r.data.Data.data, currencySymbol));
        commit('updateClerkWagerSearchStatus', {
          loading: false,
          success: true,
          error: false,
        });
      } else {
        commit('updateClerkWagerSearchStatus', {
          loading: false,
          success: false,
          error: true,
        });
      }
    });
  },
  tryScanBarcode({ commit, dispatch }, scanCodeParam) {
    if (scanCodeParam) {
      commit('setScanCode', scanCodeParam);
    }
    commit('setInvalidScanCode', false);
    const scanCode = state.scanCode.trim();
    dispatch('tryGetDepositTicketOpenBets'), dispatch('tryGetDepositTicketGradedBets');
  },
  tryScanWagerBarcode({ commit, dispatch }, scanCodeParam) {
    if (scanCodeParam) {
      commit('setScanCode', scanCodeParam);
    }
    commit('setInvalidScanCode', false);
    const scanCode = state.scanCode.trim();
    dispatch('tryGetDepositTicketOpenBets');
    dispatch('tryGetDepositTicketGradedBets');
  },
  tryGetDepositTicketOpenBets({ commit, state, rootGetters }) {
    const scanCode = state.scanCode.trim();
    if (!scanCode) return;
    commit('setClerkScanCodeStatus', { loading: true });
    getDepositTicketOpenBets({
      scan_code: scanCode,
    }).then(r => {
      if (r.data.IsSuccess) {
        const data = r.data.Data;
        const currencySymbol = rootGetters['player/currencySymbol'];
        const openBets = data.data.ticket_detail.bets.filter(bet => !bet.wager_header.is_graded);
        const gradedBets = data.data.ticket_detail.bets.filter(bet => bet.wager_header.is_graded);
        commit('setTicketOpenBets', openBets);
        commit('setTicketGradedBets', gradedBets);
        commit('setClerkScanCodeStatus', { loading: false, success: true, error: false });
        commit('depositTicketStore/setState', 'IN_WAGER', { root: true });
        commit('setScanDetailResponse', data);
      } else {
        commit('setInvalidScanCode', true);
        commit('setClerkScanCodeStatus', {
          loading: false,
          success: false,
          error: true,
        });
      }
    });
  },
  tryGetDepositTicketGradedBets({ commit, state, rootGetters }) {
    return;
    const scanCode = state.scanCode.trim();
    if (!scanCode) return;
    commit('setClerkScanCodeStatus', { loading: true });
    getDepositTicketGradedBets({
      scan_code: scanCode,
    }).then(r => {
      console.log('graded', r.data);
      if (r.data.IsSuccess) {
        const currencySymbol = rootGetters['player/currencySymbol'];
        commit('setTicketGradedBets', formatOpenBetsData(r.data.Data.data, currencySymbol));
        commit('setClerkScanCodeStatus', {
          loading: false,
          success: true,
          error: false,
        });
        commit('depositTicketStore/setState', 'IN_WAGER', { root: true });
      } else {
        commit('setInvalidScanCode', true);
        commit('setClerkScanCodeStatus', {
          loading: false,
          success: false,
          error: true,
        });
      }
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
